import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactMethods = () => {
  const contactMethods = [
    {
      id: 'email',
      icon: 'Mail',
      title: 'Email Us',
      description: 'Get detailed responses within 2 hours',
      value: 'bytepairs2026@gmail.com',
      action: 'mailto:bytepairs2026@gmail.com',
      color: 'text-primary',
      bgColor: 'bg-gradient-to-br from-primary/10 to-accent/10',
      borderColor: 'border-primary/30',
      iconBg: 'bg-primary/20'
    },
    {
      id: 'phone',
      icon: 'Phone',
      title: 'Call Us',
      description: 'Direct line for urgent inquiries',
      value: '+91 9003241548',
      action: 'tel:+919003241548',
      color: 'text-accent',
      bgColor: 'bg-gradient-to-br from-accent/10 to-warning/10',
      borderColor: 'border-accent/30',
      iconBg: 'bg-accent/20'
    },
    {
      id: 'whatsapp',
      icon: 'MessageCircle',
      title: 'WhatsApp',
      description: 'Instant messaging for quick questions',
      value: '+91 9003241548',
      action: 'https://wa.me/919003241548',
      color: 'text-success',
      bgColor: 'bg-gradient-to-br from-success/10 to-primary/10',
      borderColor: 'border-success/30',
      iconBg: 'bg-success/20'
    },
    {
      id: 'calendar',
      icon: 'Calendar',
      title: 'Schedule Call',
      description: 'Book a free 30-minute consultation',
      value: 'Available Mon-Fri',
      action: '#',
      color: 'text-warning',
      bgColor: 'bg-gradient-to-br from-warning/10 to-accent/10',
      borderColor: 'border-warning/30',
      iconBg: 'bg-warning/20'
    }
  ];

  const handleContactClick = (method) => {
    if (method.id === 'calendar') {
      // Simulate calendar booking
      alert('Calendar booking feature would open here');
      return;
    }

    if (method.id === 'whatsapp') {
      window.open(method.action, '_blank');
      return;
    }

    window.location.href = method.action;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">Choose Your Preferred Contact Method</h2>
        <p className="text-text-secondary">
          We're here to help through multiple channels. Pick what works best for you.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {contactMethods.map((method, index) => (
          <motion.div
            key={method.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            whileTap={{ scale: 0.98 }}
            className={`card p-6 border-2 ${method.borderColor} ${method.bgColor} hover:shadow-brand-lg transition-all duration-300 cursor-pointer group relative overflow-hidden`}
            onClick={() => handleContactClick(method)}
          >
            {/* Animated gradient overlay */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />

            <div className="flex items-start space-x-4 relative z-10">
              <motion.div
                className={`w-14 h-14 ${method.iconBg} rounded-xl flex items-center justify-center shadow-sm`}
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <Icon name={method.icon} size={28} className={method.color} />
              </motion.div>

              <div className="flex-1">
                <h4 className="text-lg font-bold text-foreground mb-1 group-hover:text-primary transition-colors duration-300">{method.title}</h4>
                <p className="text-sm text-text-secondary mb-2">{method.description}</p>
                <p className={`text-sm font-semibold ${method.color}`}>{method.value}</p>
              </div>

              <motion.div
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <Icon
                  name="ArrowRight"
                  size={20}
                  className="text-text-secondary group-hover:text-primary transition-colors duration-300"
                />
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-6 border border-primary/20"
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-foreground">Business Hours</h4>
            <p className="text-sm text-text-secondary">We're available when you need us</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-foreground mb-1">Monday - Friday</p>
            <p className="text-text-secondary">8:00 AM - 6:00 PM IST</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">Weekend Support</p>
            <p className="text-text-secondary">Emergency projects only</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-primary/20">
          <p className="text-sm text-text-secondary">
            <Icon name="Zap" size={16} className="inline mr-1 text-primary" />
            Average response time: <span className="font-medium text-primary">2 hours</span> during business hours
          </p>
        </div>
      </motion.div>

      <div className="text-center">
        <Button
          variant="outline"
          iconName="MessageSquare"
          iconPosition="left"
          className="border-primary/20 text-primary hover:bg-primary/5"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Try Our AI Assistant
        </Button>
      </div>
    </div>
  );
};

export default ContactMethods;